import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import { Box, Paper, Typography } from "@mui/material";

const PREFIX = "configDataBank";

const classes = {
  root: `${PREFIX}-root`,
  mainContainer: `${PREFIX}-mainContainer`,
  contentContainer: `${PREFIX}-contentContainer`,
  videoPlayer: `${PREFIX}-videoPlayer`,
  mediaText: `${PREFIX}-mediaText`,
  imageTag: `${PREFIX}-imageTag`,
  frameImageTag: `${PREFIX}-frameImageTag`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.mainContainer}`]: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "0.5rem",
  },
  [`& .${classes.contentContainer}`]: {
    display: "flex",
    height: "160px",
    // maxWidth: "650px",
    overflow: "auto",
    width: "100%",
    padding: "0.25rem 0.5rem",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },
  [`& .${classes.videoPlayer}`]: {
    height: "120px",
  },
  [`& .${classes.mediaText}`]: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: "200px",
  },
  [`& .${classes.imageTag}`]: {
    height: "120px",
  },
  [`& .${classes.frameImageTag}`]: {
    height: "140px",
    objectFit: "contain",
  },
}));

const ConfigDataBank = ({ mode, onSelectMedia }) => {
  const { t } = useTranslation();
  const objOverview = useSelector(
    (state) => state.configurator.objectsOverview
  );
  const [data, setData] = useState(null);

  const concatDataByMode = useCallback(
    (data) => {
      if (mode === "screens")
        return data
          .reduce((prev, current) => [...prev, ...current.links], [])
          .filter(
            (value, index, self) =>
              index === self.findIndex((t) => t.link === value.link)
          );
      else if (mode === "customs")
        return data
          .reduce(
            (prev, current) => [
              ...prev,
              {
                iconUrl: current.iconUrl,
                link: current.link,
                imageUrl: current.imageUrl,
              },
            ],
            []
          )
          .filter(
            (value, index, self) =>
              index === self.findIndex((t) => t.link === value.link)
          );
      else if (mode === "frames") {
        return data
          .reduce((prev, current) => {
            if (current.imageUrl || current.previewUrl) {
              return [
                ...prev,
                {
                  [current.imageUrl.includes("blob")
                    ? "previewUrl"
                    : "imageUrl"]: current.imageUrl,
                  file: current.file,
                },
              ];
            } else return prev;
          }, [])
          .filter(
            (value, index, self) =>
              index ===
              self.findIndex(
                (t) =>
                  t.imageUrl === value.imageUrl ||
                  (!t.imageUrl && t.previewUrl === value.previewUrl)
              )
          );
      } else return [];
    },
    [mode]
  );

  useEffect(() => {
    if (mode && objOverview[mode]) {
      setData(concatDataByMode(objOverview[mode]));
    }
  }, [mode, objOverview, concatDataByMode]);

  const handleMediaClick = (media) => {
    if (mode === "screens" || mode === "customs")
      onSelectMedia(media.link || media.imageUrl);
    else if (mode === "frames") onSelectMedia(media);
  };

  if (data?.length > 0)
    return (
      <Root>
        <Box className={classes.mainContainer}>
          <Typography variant="body1" sx={{ marginBottom: "0.5rem" }}>{`${t(
            `configurator.${mode}BankTitle`
          )} ${t("configurator.alreadyUsed")}`}</Typography>
          <Paper elevation={6} className={classes.contentContainer}>
            {data.map((item, index) => {
              return (
                <Box
                  key={`data-bank-${mode}-${index + 1}`}
                  onClick={() => handleMediaClick(item)}
                  sx={{
                    position: "relative",
                    border: "1px solid rgba(255, 255, 255, 0.5)",
                    borderRadius: mode !== "frames" ? "10px" : "0",
                    marginRight: "0.5rem",
                  }}
                >
                  {mode === "screens" ? (
                    <>
                      <video
                        className={classes.videoPlayer}
                        preload="auto"
                        // controls={false}
                        // add #t=Time to automatically display thumbnail from the video
                        src={item.link + "#t=0.01"}
                      />
                      <Typography variant="body1" className={classes.mediaText}>
                        {item.name}
                      </Typography>
                    </>
                  ) : mode === "customs" ? (
                    <img
                      alt={`preview 3D model of ${
                        item.iconUrl || item.link || item.imageUrl
                      }`}
                      src={item.iconUrl || item.imageUrl}
                      className={classes.imageTag}
                    />
                  ) : (
                    <img
                      alt={`frame preview of ${item}`}
                      src={item.imageUrl || item.previewUrl}
                      className={classes.frameImageTag}
                    />
                  )}
                </Box>
              );
            })}
          </Paper>
        </Box>
      </Root>
    );
  else return null;
};

export default ConfigDataBank;
